import { ProjectPayload, ProjectState, FeatureStatusPayload } from "./useProjects.types";
import { isProjectTrial } from "utils/projectUtils";

type ProjectInfoPayload = NonNullable<NonNullable<ProjectPayload["permissions"]>["projectInfo"]>;

function readEmbeddedProjectInfo(project: ProjectPayload): ProjectInfoPayload | undefined {
  const raw = project.permissions as Record<string, unknown> | undefined;
  if (!raw) return undefined;
  return (raw.projectInfo ?? raw.project_info) as ProjectInfoPayload | undefined;
}

/** Backend findOne/create returns project.documents; list with include_versions may return versions */
type ProjectPayloadWithDocuments = ProjectPayload & {
  documents?: Array<{
    document_id: string;
    title?: string;
    version?: string;
    created_at: string;
  }>;
};

/**
 * Transform API project data to frontend format
 */
export const transformProjectData = (project: ProjectPayload): ProjectState => {
  const isTrial = isProjectTrial(project);
  const isLicensed = !isTrial;

  return {
    projectId: project.project_id,
    name: project.name,
    description: project.description,
    productionType: project.production_type,
    slug: (project as any).slug,
    projectImagePath: project.project_image_path,
    createdAt: project.created_at,
    archived: project.archived,
    deletedAt: project.deleted_at,
    isDeleted: project.is_deleted,
    status: project.status,
    workspaceId: String(project.workspace_id),
    purchaseType: project.purchase_type,
    oneTimePurchaseId: project.one_time_purchase_id,
    trialExpiryDate: project.trial_expiry_date,
    isTrial: isTrial,
    isLicensed,
    isDemo: project.is_demo ?? false,
    scheduleRerunsLeft: (project as { schedule_reruns_left?: number }).schedule_reruns_left,
    budgetRerunsLeft: (project as { budget_reruns_left?: number }).budget_reruns_left,
    aiCredits: project.ai_credits
      ? {
          remaining: project.ai_credits.remaining,
          usedTotal: project.ai_credits.used_total,
          walletKind: project.ai_credits.wallet_kind,
          unlimited: project.ai_credits.unlimited === true,
          actionCosts: project.ai_credits.action_costs,
          actionTypes: project.ai_credits.action_types,
          staleness: project.ai_credits.staleness,
        }
      : undefined,
    otpSeating: project.otp_seating
      ? {
          collaboratorCap: project.otp_seating.collaborator_cap,
          seatCapTotal: project.otp_seating.seat_cap_total,
          collaboratorsInvited: project.otp_seating.collaborators_invited,
          seatsUsed: project.otp_seating.seats_used,
          licenseTier: project.otp_seating.license_tier,
        }
      : undefined,
    apiKeyAccess: project.api_key_access
      ? {
          allowed: project.api_key_access.allowed,
          blockedReason: project.api_key_access.blocked_reason,
          message: project.api_key_access.message,
        }
      : undefined,
    // Versioning: backend may return `versions` (list) or `documents` (findOne/create)
    versionsCount: project.versions_count,
    activeDocumentId: project.active_document_id ?? null,
    versions: (project.versions ?? (project as ProjectPayloadWithDocuments).documents)?.map(
      (v: { document_id: string; title?: string; version?: string; created_at: string }) => ({
        documentId: v.document_id,
        title: v.title ?? "",
        version: v.version ?? "",
        createdAt: typeof v.created_at === "string" ? v.created_at : ((v as any).created_at ?? ""),
      })
    ),
    features: {
      projectFeatureId: project.features?.project_feature_id ?? 0,
      projectId: project.features?.project_id ?? project.project_id,
      hasCoverage: project.features?.has_coverage ?? false,
      hasCharacterBreakdown: project.features?.has_character_breakdown ?? false,
      hasScriptBreakdown: project.features?.has_script_breakdown ?? false,
      hasBudget: project.features?.has_budget ?? false,
      hasSchedule: project.features?.has_schedule ?? false,
      scheduleStartDate: project.features?.schedule_start_date,
      minBudget: project.features?.min_budget,
      maxBudget: project.features?.max_budget,
      productionState: project.features?.production_state,
    },
    featureStatuses: project.feature_statuses?.map((status: FeatureStatusPayload) => ({
      featureStatusId: status.feature_status_id,
      documentId: status.document_id,
      feature: status.feature,
      status: status.status,
      metadata: status.metadata,
    })),
    team: project.team
      ? {
          teamId: project.team.team_id,
          members: project.team.members,
        }
      : undefined,
    user: project.user,
    currentUserRole: project.current_user_role,
    permissions: project.permissions
      ? (() => {
          const pi = readEmbeddedProjectInfo(project);
          const row = (pi ?? {}) as Record<string, unknown>;
          return {
            permissions: project.permissions.permissions ?? [],
            projectInfo: {
              isLicensed: Boolean(row.isLicensed ?? row.is_licensed ?? !row.isTrialProject),
              isTrialProject: Boolean(row.isTrialProject ?? row.is_trial_project),
              trialExpiryDate: (row.trialExpiryDate ?? row.trial_expiry_date) as string | undefined,
              canUpgrade: Boolean(row.canUpgrade ?? row.can_upgrade),
              projectId: String(row.projectId ?? row.project_id ?? project.project_id),
              projectName: (row.projectName ?? row.project_name ?? project.name) as string,
              freeLicensePremiumLocked: Boolean(
                row.freeLicensePremiumLocked ?? row.free_license_premium_locked
              ),
              unlicensedPremiumLocked: Boolean(
                row.unlicensedPremiumLocked ?? row.unlicensed_premium_locked
              ),
              unlicensedMaxDraftScenes:
                typeof row.unlicensedMaxDraftScenes === "number" &&
                Number.isFinite(row.unlicensedMaxDraftScenes)
                  ? row.unlicensedMaxDraftScenes
                  : typeof row.unlicensed_max_draft_scenes === "number" &&
                      Number.isFinite(row.unlicensed_max_draft_scenes)
                    ? row.unlicensed_max_draft_scenes
                    : undefined,
              createSource: (() => {
                const fromRow = row.createSource ?? row.create_source;
                if (typeof fromRow === "string" && fromRow.trim() !== "") {
                  return fromRow.trim();
                }
                const root = (project as { create_source?: string }).create_source;
                return typeof root === "string" && root.trim() !== "" ? root.trim() : undefined;
              })(),
            },
            roles: {
              workspaceRole: project.permissions.roles?.workspaceRole ?? "viewer",
              projectRole: project.permissions.roles?.projectRole,
            },
          };
        })()
      : undefined,
  };
};

/**
 * Transform array of API project data to frontend format
 */
export const transformProjectArray = (projects: ProjectPayload[]): ProjectState[] => {
  return projects
    .filter((project) => project && typeof project === "object")
    .map(transformProjectData);
};
